import { useCallback } from 'react';
import { useEffect } from 'react';
import { useRef } from 'react';
import { createBroadcastChannelFactory } from './broadcast-channel-factory';
import { homePath } from './constants';
import { createCrossTabBroadcaster } from './session-core';
import { sessionChannelName } from './use-session-broadcast';
import { OrganizationSelections } from '@socialgoodsoftware/libraries/services/organization-selection';
import type { CrossTabBroadcaster } from './session-core';
import type { OrganizationSelection } from './types';

type UseOrgSyncOptions = {
  selectedOrganization: OrganizationSelection | null | undefined;
  onSeed: (selection: OrganizationSelection) => void;
  onResync?: (selection: OrganizationSelection) => void;
};

type UseOrgSyncResult = {
  broadcastOrgSwitch: (selection: OrganizationSelection) => void;
  reactToOrganizationSelection: (selection: OrganizationSelection) => void;
};

export function useOrgSync({ selectedOrganization, onSeed, onResync }: UseOrgSyncOptions): UseOrgSyncResult {
  const selectedRef = useRef(selectedOrganization);
  useEffect(() => {
    selectedRef.current = selectedOrganization;
  }, [selectedOrganization]);

  const onSeedRef = useRef(onSeed);
  useEffect(() => {
    onSeedRef.current = onSeed;
  }, [onSeed]);

  const onResyncRef = useRef(onResync);
  useEffect(() => {
    onResyncRef.current = onResync;
  }, [onResync]);

  const reloadTriggeredRef = useRef(false);
  const broadcasterRef = useRef<CrossTabBroadcaster | null>(null);

  const reactToOrganizationSelection = useCallback((selection: OrganizationSelection | null | undefined): void => {
    if (!reloadTriggeredRef.current && selection) {
      const currentSelection = selectedRef.current;
      if (!currentSelection) {
        onSeedRef.current(selection);
      } else if (!OrganizationSelections.IsSame(currentSelection, selection)) {
        const resync = onResyncRef.current;
        if (resync) {
          resync(selection);
        } else {
          reloadTriggeredRef.current = true;
          window.location.assign(homePath);
        }
      }
    }
  }, []);

  useEffect(() => {
    const broadcaster = createCrossTabBroadcaster({
      channelName: sessionChannelName,
      createChannel: createBroadcastChannelFactory,
      onOrgSwitch: (selection) => {
        reactToOrganizationSelection(selection);
      },
    });
    broadcasterRef.current = broadcaster;

    return () => {
      broadcaster.close();
      broadcasterRef.current = null;
    };
  }, [reactToOrganizationSelection]);

  const broadcastOrgSwitch = useCallback((selection: OrganizationSelection): void => {
    if (broadcasterRef.current) {
      broadcasterRef.current.broadcastOrgSwitch(selection);
    }
  }, []);

  return {
    broadcastOrgSwitch: broadcastOrgSwitch,
    reactToOrganizationSelection: reactToOrganizationSelection,
  };
}

export function broadcastOrgSwitch(selection: OrganizationSelection): void {
  const broadcaster = createCrossTabBroadcaster({
    channelName: sessionChannelName,
    createChannel: createBroadcastChannelFactory,
  });
  broadcaster.broadcastOrgSwitch(selection);
  broadcaster.close();
}
